import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function DailyLogicPuzzlesAppPage() {
  const reviews = [
    {
      author: 'Zonthea',
      rating: '★★★★★',
      text: "I appreciate the developer listening to feedback and making updates to the game to make it better. Love this app!",
      source: 'App Store'
    },
    {
      author: 'Bethanatorrrrrrrrr',
      rating: '★★★★★',
      text: "With the new update, the bugs with sudoku have been fixed and I'm so happy to continue playing this game.",
      source: 'App Store'
    }
  ];

  const features = [
    { title: '8 Daily Puzzles', description: 'Fresh puzzles every day: Crowns, Binary Sudoku, Mine Finder, Sudoku, Tiny Sudoku, Color Flood, Aquarium, and Dot Stream.' },
    { title: 'Leaderboards', description: 'Compete with players worldwide and challenge your friends with the new friends leaderboard.' },
    { title: 'Personal Bests', description: 'Track your improvement with PB indicators and detailed stats for each game.' },
    { title: 'Archive Mode', description: 'Missed a day? Play any past puzzle from the archives.' },
    { title: 'Minimal Ads', description: 'Play first, not ads. We keep interruptions to a minimum so you can focus on solving.' },
    { title: 'Zen Mode', description: 'Play with no timer and no stress - just pure puzzle enjoyment.' }
  ];

  const games = [
    { name: 'Crowns', description: 'One crown per row/column/region; crowns can\'t touch.' },
    { name: 'Binary Sudoku', description: '0s/1s: no three in a row; balanced rows & columns.' },
    { name: 'Mine Finder', description: 'Clear the board, flag the mines.' },
    { name: 'Sudoku', description: 'The timeless number puzzle.' },
    { name: 'Tiny Sudoku', description: 'Bite-size boards for quick wins.' },
    { name: 'Color Flood', description: 'Fill the board in as few moves as possible.' },
    { name: 'Aquarium', description: 'Fill regions up to the waterline.' },
    { name: 'Dot Stream', description: 'Connect numbered dots into one path.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Daily Logic Puzzles - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Daily Logic Puzzles for iOS and Android. 8 new puzzles every day including Crowns, Sudoku, Mine Finder, and more. 4.7-star App Store rating with minimal ads." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/daily-logic-puzzles" />
        <meta property="og:title" content="Daily Logic Puzzles - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Daily Logic Puzzles. 8 new puzzles every day including Crowns, Sudoku, and Mine Finder." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/daily-logic-puzzles" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Daily Logic Puzzles - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Daily Logic Puzzles. 8 new puzzles every day including Crowns, Sudoku, and Mine Finder." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Daily Logic Puzzles</h1>
            <p className="app-tagline">One app. Eight daily logic puzzles.</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4.7 ★</span>
                <span className="app-detail-stat-label">Rating</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">10K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">8</span>
                <span className="app-detail-stat-label">Daily Games</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('dlp', 'apps-page', '/apps/daily-logic-puzzles')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'dlp', store: 'app_store', placement: 'apps-page', page: '/apps/daily-logic-puzzles' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('dlp', 'apps-page', '/apps/daily-logic-puzzles')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'dlp', store: 'google_play', placement: 'apps-page', page: '/apps/daily-logic-puzzles' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/dailylogicpuzzles_logo.webp"
              alt="Daily Logic Puzzles App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Daily Logic Puzzles</h2>
          <p className="app-detail-description">
            New logic puzzles every single day. Clean design, minimal ads, just satisfying solves - plus leaderboards 
            to push your best. Whether you have 5 minutes or an hour, Daily Logic Puzzles fits perfectly into your routine 
            with quick sessions that challenge your mind without overwhelming your schedule.
          </p>
        </section>

        <section className="app-detail-section">
          <h2>8 Games, Fresh Daily</h2>
          <div className="app-features-grid">
            {games.map((game, index) => (
              <div key={index} className="app-feature-card">
                <h3>{game.name}</h3>
                <p>{game.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How It Works</h2>
          <p className="app-detail-description">
            Every day at midnight, eight new puzzles unlock across a variety of logic genres. Solve them at
            your own pace - each puzzle tracks your completion time so you can compete on leaderboards or
            simply try to beat your personal best. Missed a day? The archive lets you go back and play any
            puzzle from any past date, so you never miss a challenge.
          </p>
          <h3>Why Daily Puzzles?</h3>
          <ul className="app-detail-list">
            <li>Building a daily puzzle habit strengthens working memory, pattern recognition, and logical reasoning over time.</li>
            <li>Eight different puzzle types keep your brain engaged by switching between different kinds of thinking.</li>
            <li>Short sessions fit easily into a morning routine, commute, or coffee break.</li>
            <li>Leaderboards and personal bests add friendly competition without pressure - play at your pace.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Daily Logic Puzzles and start your daily brain training!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/dailylogicpuzzles" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default DailyLogicPuzzlesAppPage;
